import { useRef, useLayoutEffect } from 'react'
import useScrollDirection from './useScrollDirection'
import useStateRef from './useStateRef'
import { Primitive } from '@/types/global'

interface IProps<T extends Primitive> {
  elements: { element: HTMLElement; key: T }[]
  onCurrentObservingElementChange: (key: T) => void
  active: boolean
  root?: HTMLElement
}

const useActiveObservableElement = <T extends Primitive>(props: IProps<T>) => {
  const scrollDirectionState = useScrollDirection(props.root)
  const scrollDirectionRef = useStateRef(scrollDirectionState)

  const scrollLinksQueue = useRef<T[]>([])
  const intersectionRatioState = useRef<Partial<Record<T, number>>>({})

  useLayoutEffect(() => {
    if (!props.active || props.elements?.length === 0) return

    const observers = []

    for (const el of props.elements) {
      const targetElement = el.element

      if (!targetElement) continue

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ isIntersecting, intersectionRatio }) => {
            intersectionRatioState.current[el.key] = intersectionRatio

            const isInTheQueue = scrollLinksQueue.current.findIndex((it) => it === el.key) !== -1

            // Если статья вышла из вьюпорта, то мы ее удаляем
            if (!isIntersecting) {
              const queueItemIndex = scrollLinksQueue.current.findIndex((it) => it === el.key)
              if (queueItemIndex === -1) return
              scrollLinksQueue.current.splice(queueItemIndex, 1)

              if (scrollDirectionRef.current === 'up') {
                props.onCurrentObservingElementChange(scrollLinksQueue.current.at(-1))
              } else {
                props.onCurrentObservingElementChange(scrollLinksQueue.current[0])
              }

              return
            } else {
              // Если не в очереди, то доблавяем в начало либо конец в зависимости от scroll direction
              if (!isInTheQueue) {
                if (scrollDirectionRef.current === 'up') {
                  scrollLinksQueue.current.push(el.key)
                  scrollLinksQueue.current = scrollLinksQueue.current.sort(
                    (a, b) => (intersectionRatioState.current[a] || 0) - (intersectionRatioState.current[b] || 0)
                  )

                  props.onCurrentObservingElementChange(scrollLinksQueue.current.at(-1))
                } else {
                  scrollLinksQueue.current.unshift(el.key)

                  props.onCurrentObservingElementChange(scrollLinksQueue.current[0])
                }
              } else {
                if (scrollDirectionRef.current === 'up') {
                  scrollLinksQueue.current = scrollLinksQueue.current.sort(
                    (a, b) => (intersectionRatioState.current[a] || 0) - (intersectionRatioState.current[b] || 0)
                  )
                  props.onCurrentObservingElementChange(scrollLinksQueue.current.at(-1))
                }
              }
            }
          })
        },
        {
          root: props.root,
          threshold: Array.from({ length: 11 }, (_, i) => i / 10),
        }
      )

      observers.push(observer)
      observer.observe(targetElement)
    }

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [props.elements, props.root])
}

export default useActiveObservableElement
